import { useCallback, type KeyboardEvent } from 'react';

/**
 * Enter moves to the next field.
 *
 * Anybody who has entered data in Tally or Marg for years does it without
 * looking: type, Enter, type, Enter. In a browser Enter submits the form
 * instead, which either posts a half-filled document or does nothing, and both
 * feel broken to somebody whose hands already know what should happen.
 *
 * So Enter walks the fields in order and only submits from the last one — or
 * from a button, which is where a mouse user expects it to work. A textarea is
 * left alone, because Enter there means a new line.
 */
export function useKeyboardForm(): (e: KeyboardEvent<HTMLFormElement>) => void {
  return useCallback((e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;

    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') return;

    const form = e.currentTarget;
    const fields = [...form.querySelectorAll<HTMLElement>('input, select, textarea')].filter(
      (el) => !(el as HTMLInputElement).disabled && el.tabIndex !== -1 && el.offsetParent !== null,
    );

    const here = fields.indexOf(target);
    if (here === -1) return;

    const next = fields[here + 1];
    if (!next) return; // the last field: let Enter submit, as it should

    e.preventDefault();
    next.focus();
    if (next instanceof HTMLInputElement) next.select();
  }, []);
}
